import React from 'react';
import PropTypes from 'prop-types';
import Media from 'react-media';

import trackAction from 'utils/tracking';
import { slugify } from 'utils/string';

import { getPageOnHostByID } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';
import { ACTION_NODE_LINK } from 'constants/Action';

import ActiveLink from 'components/Core/ActiveLink';
import Breadcrumbs from 'components/Core/Breadcrumbs';
import Carousel from 'components/Core/Carousel';
import Cta from 'components/Core/Cta';
import Embed from 'components/Core/Embed';
import RichText from 'components/Core/RichText';
import CtaBlock from 'components/CtaBlock';
import Contact from 'components/Contact';
import Faq from 'components/Faq';
import ServiceCard from 'components/ServiceCard';

import {
  ServicePageFigure as ServicePageFigureElem,
  CheckListElem,
  ServiceLinkElem,
  ServiceLinkWrapElem,
} from './ServicePage.styles';

const mediaQueries = {
  sm: '(max-width: 767px)',
  md: '(min-width: 768px) and (max-width: 1023px)',
  lg: '(min-width: 1024px)',
};

const onServiceClick = (serviceCategory, serviceName) => {
  trackAction('service-detail', serviceName, serviceCategory);
};

const getOtherServices = (serviceCat) => {
  return (
    <div key={serviceCat.id} className="flex-full max-w-full lg:flex-1/3 lg:max-w-1/3 px-3 my-4 h-98per">
      <ServiceCard {...serviceCat} rigidHeight onServiceClickCB={onServiceClick} />
    </div>
  );
};

const getServiceLink = (service, categoryName) => {
  return (
    <ServiceLinkWrapElem key={service.id} className="font-bold py-rem375 md:py-3 leading-8 md:leading-5">
      <ActiveLink href="/[...slug]" as={service.url} passHref activeClassName="border-primary">
        <ServiceLinkElem
          className="border-b-2 border-transparent"
          onClick={() => onServiceClick(categoryName, service.title)}
        >
          {service.title}
        </ServiceLinkElem>
      </ActiveLink>
    </ServiceLinkWrapElem>
  );
};

const getServiceCta = (linkTitle = '', link, serviceTitle) => {
  const linkBlock = link[0];
  const { link: linkItems } = linkBlock;
  const ctaLink = linkItems[0];
  const { type: linkType } = ctaLink;

  let urlHref;

  const isExternalUrl = linkType === 'URLBlock';

  if (isExternalUrl) {
    const { value: url } = ctaLink;
    urlHref = url;
  } else {
    const {
      page: { url },
    } = ctaLink;
    urlHref = url;
  }

  return (
    <>
      {isExternalUrl && (
        <Cta
          className="w-full md:w-auto"
          node={ACTION_NODE_LINK}
          external
          href={urlHref}
          showIcon={false}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackAction('service-detail', linkTitle, serviceTitle)}
          onKeyPress={() => trackAction('service-detail', linkTitle, serviceTitle)}
        >
          {linkTitle}
        </Cta>
      )}
      {!isExternalUrl && (
        <Cta
          className="w-full md:w-auto"
          node={ACTION_NODE_LINK}
          href="/[...slug]"
          as={urlHref}
          showIcon={false}
          onClick={() => trackAction('service-detail', linkTitle, serviceTitle)}
          onKeyPress={() => trackAction('service-detail', linkTitle, serviceTitle)}
        >
          {linkTitle}
        </Cta>
      )}
    </>
  );
};

const ServicePage = (props) => {
  const {
    page: {
      title,
      image,
      bodyRt,
      introduction,
      category: { name: categoryName, services: categoryServices },
      otherServices = [{ title: '', categories: null }],
      breadcrumbs,
      ctaTitle,
      ctaLink,
      checklistTitle,
      checklist = [],
      optionalBody,
    },
    hostname,
  } = props;

  const [otherServiceBlock] = otherServices.length ? otherServices : [{ title: '', categories: null }];

  const { title: otherServicesTitle, categories: serviceCategories } = otherServiceBlock;

  return (
    <>
      <div className="container mx-auto px-4">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <div>
          <h2 className="h1 mb-4 text-secondary">{categoryName}</h2>
          <h1 className="h3 mb-6 lg:mb-10">{title}</h1>
          <div className="flex flex-wrap lg:justify-between -mx-4">
            <div className="flex-full lg:flex-7/12 max-w-full lg:max-w-7/12 p-4">
              {image && (
                <ServicePageFigureElem className="mb-8">
                  <Media queries={mediaQueries} defaultMatches={{ lg: true }}>
                    {(matches) => {
                      if (matches.sm) {
                        return (
                          <img
                            src={image.sm.url}
                            alt={image.title}
                            className="w-full h-full rounded-lg object-cover object-center"
                          />
                        );
                      }
                      return (
                        <img
                          src={image.lg.url}
                          alt={image.title}
                          className="w-full h-full rounded-lg object-cover object-center"
                        />
                      );
                    }}
                  </Media>
                </ServicePageFigureElem>
              )}
              <p className="font-bold mb-6 lg:mb-10">{introduction}</p>
              <div>
                <RichText blockMargin={false} content={bodyRt} />
                {ctaLink.length > 0 && <div className="mb-6 lg:mb-10">{getServiceCta(ctaTitle, ctaLink, title)}</div>}
              </div>
            </div>
            <aside className="flex-full lg:flex-1/3 max-w-full lg:max-w-1/3">
              <div className="m-4 p-6 md:p-8 bg-activity-sport-400 rounded-base mb-8 text-basic-700">
                <h2 className="h3 mb-6 lg:mb-8">{categoryName}</h2>
                <ul className="pb-6 md:pb-8">
                  {categoryServices.map((service) => getServiceLink(service, categoryName))}
                </ul>
              </div>
              {checklist.length > 0 && (
                <div className="m-4 mt-8">
                  {checklistTitle && <h2 className="h3 mb-6 lg:mb-10 text-basic-700">{checklistTitle}</h2>}
                  <ul>
                    {(checklist || []).map(({ content: item }) => (
                      <CheckListElem key={slugify(item)} className="items-center list-none mb-3 px-2">
                        {item}
                      </CheckListElem>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
      {(optionalBody || []).map((block) => {
        if (block.blockType === 'CTABlock') {
          return <CtaBlock {...block} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'FAQBlock') {
          return <Faq key={`${block.blockType}-${block.id}`} {...block} />;
        }
        if (block.blockType === 'ContactBlock') {
          return <Contact hostname={hostname} {...block} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'CustomEmbedBlock') {
          return (
            <div key={`${block.blockType}-${block.id}`} className="container mx-auto px-4">
              <div className="u-block-margins">
                <Embed title={block.title} {...block.embed} />
              </div>
            </div>
          );
        }
        return <div key={`${block.blockType}-${block.id}`} />;
      })}
      <div className="container mx-auto px-4">
        {serviceCategories && (
          <div className="u-block-margins">
            <h2 className="h3 text-basic-700 mb-4 lg:mb-8">{otherServicesTitle}</h2>
            <Media queries={mediaQueries} defaultMatches={{ lg: true }}>
              {(matches) => {
                if (matches.lg) {
                  return <div className="flex flex-wrap -mx-4">{serviceCategories.map(getOtherServices)}</div>;
                }
                if (matches.md) {
                  return (
                    <Carousel
                      items={serviceCategories}
                      totalNumber={serviceCategories.length}
                      visibleSlides={2}
                      slideRenderer={getOtherServices}
                    />
                  );
                }
                return (
                  <Carousel
                    items={serviceCategories}
                    totalNumber={serviceCategories.length}
                    slideRenderer={getOtherServices}
                  />
                );
              }}
            </Media>
          </div>
        )}
      </div>
    </>
  );
};

ServicePage.getCustomInitialProps = async ({ id, contentType, hostname, previewData }) => {
  const query = requireQueryByName('ServicePage');
  const token = previewData ? previewData.token : '';
  const { page } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
  });

  return {
    page,
    extraPageProps: {
      hostname,
    },
  };
};

ServicePage.propTypes = {
  page: PropTypes.shape({
    title: PropTypes.string.isRequired,
    bodyRt: PropTypes.string.isRequired,
    introduction: PropTypes.string.isRequired,
    category: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          title: PropTypes.string,
          slug: PropTypes.string,
          url: PropTypes.string,
        }),
      ),
    }).isRequired,
    image: PropTypes.shape({
      title: PropTypes.string,
      lg: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      sm: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
    }),
    otherServices: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        categories: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    ),
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
      }),
    ).isRequired,
    ctaTitle: PropTypes.string,
    ctaLink: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        link: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.shape({
              type: PropTypes.string.isRequired,
              page: PropTypes.shape({
                id: PropTypes.string.isRequired,
                url: PropTypes.string.isRequired,
              }),
            }),
            PropTypes.shape({
              type: PropTypes.string.isRequired,
              value: PropTypes.string.isRequired,
              id: PropTypes.string.isRequired,
            }),
          ]),
        ),
      }),
    ),
    checklistTitle: PropTypes.string,
    checklist: PropTypes.arrayOf(PropTypes.shape({ content: PropTypes.string })),
    optionalBody: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  hostname: PropTypes.string.isRequired,
};

export default ServicePage;
